/**
 * Bootstrap Collapse replacement - Plain JavaScript
 * Compatible with Bootstrap 4 collapse markup and attributes
 */

// Store callbacks for specific elements (shared across all instances)
const callbacks = new Map();

const Collapse = () => {
  const TRANSITION_DURATION = 350;
  const SELECTOR_DATA_TOGGLE = '[data-toggle="collapse"]';

  const collapse = (element, toggle) => {
    if (element.classList.contains("collapsing")) return;

    // Trigger before collapse callback
    const elementCallbacks = callbacks.get(element);
    if (elementCallbacks && elementCallbacks.onCollapse) {
      elementCallbacks.onCollapse(element, toggle);
    }

    element.classList.remove("collapse");
    element.classList.add("collapsing");
    element.style.height = `${element.scrollHeight}px`;
    element.removeAttribute("data-skip-playback");

    // Update toggle button aria-expanded
    if (toggle) {
      toggle.setAttribute("aria-expanded", "true");
      toggle.classList.remove("collapsed");
    }

    setTimeout(() => {
      element.classList.remove("collapsing");
      element.classList.add("collapse", "show");
      element.style.height = "";

      // Trigger after collapse callback
      const elementCallbacks = callbacks.get(element);
      if (elementCallbacks && elementCallbacks.onCollapsed) {
        elementCallbacks.onCollapsed(element, toggle);
      }
    }, TRANSITION_DURATION);
  };

  const expand = (element, toggle) => {
    if (element.classList.contains("collapsing")) return;

    // Trigger before expand callback
    const elementCallbacks = callbacks.get(element);
    if (elementCallbacks && elementCallbacks.onExpand) {
      elementCallbacks.onExpand(element, toggle);
    }

    element.style.height = `${element.scrollHeight}px`;
    element.classList.add("collapsing");
    element.classList.remove("collapse", "show");
    element.setAttribute("data-skip-playback", "true");

    // Update toggle button aria-expanded
    if (toggle) {
      toggle.setAttribute("aria-expanded", "false");
      toggle.classList.add("collapsed");
    }

    // Force reflow
    element.offsetHeight;

    element.style.height = "";

    setTimeout(() => {
      element.classList.remove("collapsing");
      element.classList.add("collapse");

      // Trigger after expand callback
      const elementCallbacks = callbacks.get(element);
      if (elementCallbacks && elementCallbacks.onExpanded) {
        elementCallbacks.onExpanded(element, toggle);
      }
    }, TRANSITION_DURATION);
  };

  const toggle = (element, toggleButton) => {
    if (element.classList.contains("show")) {
      expand(element, toggleButton);
    } else {
      collapse(element, toggleButton);
    }
  };

  const on = (elementOrSelector, event, callback) => {
    const element =
      typeof elementOrSelector === "string"
        ? document.querySelector(elementOrSelector)
        : elementOrSelector;

    if (!element) return;

    if (!callbacks.has(element)) {
      callbacks.set(element, {});
    }

    const elementCallbacks = callbacks.get(element);
    elementCallbacks[event] = callback;
  };

  const handleClick = (e) => {
    const toggleButton = e.currentTarget;
    const target = toggleButton.getAttribute("data-target") || toggleButton.getAttribute("href");
    if (!target) return;
    e.preventDefault();

    const element = document.querySelector(target);
    if (!element) return;

    const group = toggleButton.dataset.collapseGroup;
    if (group) {
      document
        .querySelectorAll(`${SELECTOR_DATA_TOGGLE}[data-collapse-group="${group}"]`)
        .forEach((btn) => {
          if (btn === toggleButton) return;
          const otherTarget = btn.getAttribute("data-target") || btn.getAttribute("href");
          const otherEl = document.querySelector(otherTarget);
          if (otherEl && otherEl.classList.contains("show")) {
            expand(otherEl, btn); // close the previously open panel
          }
        });
    }

    toggle(element, toggleButton);
  };

  const init = () => {
    const toggleButtons = document.querySelectorAll(SELECTOR_DATA_TOGGLE);

    toggleButtons.forEach((button) => {
      button.addEventListener("click", handleClick);

      // Set initial collapsed class
      const target = button.getAttribute("data-target") || button.getAttribute("href");
      const element = document.querySelector(target);

      if (element && !element.classList.contains("show")) {
        button.classList.add("collapsed");
        button.setAttribute("aria-expanded", "false");
        element.setAttribute("data-skip-playback", "true");
      } else {
        button.setAttribute("aria-expanded", "true");
      }
    });
  };

  return { init, toggle, collapse, expand, on };
};

export default Collapse;
